import React, { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff } from 'lucide-react'; // Import icons

const RegisterForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { register: registerUser, isLoading, error: authError } = useAuth();
  // const navigate = useNavigate(); // Remove or comment out if not used

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null); // Clear local errors

    if (password !== password2) {
      setError("Passwords do not match.");
      return;
    }

    try {
      await registerUser(email, password, password2);
      // Potentially navigate after successful registration, uncomment above line then
      // navigate('/dashboard'); // Or wherever appropriate
    } catch (err: any) {
      // Error is now handled and displayed by the context provider
      console.error("Registration failed component-level catch:", err);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <CardTitle>Create Account</CardTitle>
        <CardDescription>
          Enter your email and password to create an account.
          <br />
          Or <Link to="/login" className="underline">sign in to your existing account</Link>
        </CardDescription>
      </CardHeader>
      <CardContent>
        {(authError || error) && (
          <div className="text-red-500 text-sm p-3 bg-red-100 rounded">
            {error || authError}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email address</Label>
            <Input 
              id="email" 
              type="email" 
              placeholder="you@example.com" 
              required 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
          </div>
          <div className="space-y-2 relative"> {/* Add relative positioning */} 
            <Label htmlFor="password">Password</Label>
            <Input 
              id="password" 
              type={showPassword ? 'text' : 'password'} 
              required 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              className="pr-10" // Add padding for icon
            />
            <Button 
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 mt-3.5" // Position icon
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              tabIndex={-1} // Make button unfocusable
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
          <div className="space-y-2 relative"> {/* Add relative positioning */} 
            <Label htmlFor="confirm-password">Confirm Password</Label>
            <Input 
              id="confirm-password" 
              type={showPassword2 ? 'text' : 'password'} // Use separate state
              required 
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              disabled={isLoading}
              className="pr-10" // Add padding for icon
            />
            <Button 
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 mt-3.5" // Position icon
              onClick={() => setShowPassword2(!showPassword2)}
              aria-label={showPassword2 ? "Hide password" : "Show password"}
              tabIndex={-1} // Make button unfocusable
            >
              {showPassword2 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Registering...' : 'Register'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default RegisterForm; 